import { z } from 'zod';
import type { McpTransportSchema } from '@terra/shared-contracts-mcp-servers';
import { PaginationQueryParamsSchema } from '@terra/shared-dto-types';
import { UpsertMcpCredentialSchema } from '@terra/shared-contracts-mcp-servers';
import { JsonValueSchema } from '@terra/shared-schemas-orchestrator';

export const AiAssetSchema = z.object({
  aiFlowGraphId: z.string(),
  aiFlowNodeId: z.string(),
});

export type AiAsset = z.infer<typeof AiAssetSchema>;

export const AiApplicationKindSchema = z.enum(['AGENT', 'MCP_SERVER']);
export const AI_APPLICATION_MCP_TRANSPORTS = [
  'SSE',
  'HTTP',
] as const satisfies readonly z.infer<typeof McpTransportSchema>[];
export const AiApplicationMcpTransportSchema = z.enum(
  AI_APPLICATION_MCP_TRANSPORTS,
);
export const AiApplicationDiscoveryModeSchema = z.enum([
  'CODE',
  'BLACK_BOX',
  'HYBRID',
  'MANUAL',
]);
export const AiApplicationDiscoveryStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'COMPLETE',
  'FAILED',
]);
export const AiApplicationInterfaceTypeSchema = z.enum([
  'HTTP',
  'SSE',
  'WEBSOCKET',
  'MCP',
  'VOICE',
]);
export const AiComponentTypeSchema = z.enum([
  'AGENT',
  'SUB_AGENT',
  'TOOL',
  'MCP_SERVER',
  'MCP_TOOL',
  'MCP_PROMPT',
  'MCP_RESOURCE',
  'MCP_PROTOCOL',
  'RAG',
  'MODEL',
  'MEMORY',
  'GUARDRAIL',
  'INTEGRATION',
]);
export const AiComponentRelationTypeSchema = z.enum([
  'DELEGATES_TO',
  'CALLS_TOOL',
  'CALLS_MCP_TOOL',
  'CALLS_MCP_PROMPT',
  'CALLS_MCP_RESOURCE',
  'CALLS_MCP_PROTOCOL',
  'QUERIES_RAG',
  'USES_MODEL',
  'READS_MEMORY',
  'WRITES_MEMORY',
  'EXPOSES_INTERFACE',
  'REQUIRES_APPROVAL',
  'CROSSES_TRUST_BOUNDARY',
]);

export const AiComponentProvenanceSchema = z.enum([
  'CODE',
  'DISCOVERY',
  'HYBRID',
  'MANUAL',
]);

export const AiComponentVerificationStatusSchema = z.enum([
  'UNVERIFIED',
  'CODE_ONLY',
  'RUNTIME_VERIFIED',
  'CONTRADICTED',
  'FAILED',
  'SUPPRESSED',
]);

export const AiComponentSourceSchema = z.enum([
  'SOURCE_CODE',
  'DISCOVERY',
  'MANUAL',
  'OPERATOR',
]);

export const AiApplicationInterfaceMetadataSchema = z
  .looseObject({
    discoveryPending: z.boolean().optional(),
    discoveredAt: z.iso.datetime().optional(),
    probeStatus: z
      .enum(['PENDING', 'VERIFIED', 'FAILED', 'SKIPPED'])
      .optional(),
    discoveryFailureReason: z.string().optional(),
    bodyExample: z.string().optional(),
  })
  .nullable()
  .optional();

const AiApplicationInterfaceBaseSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  url: z.url(),
  type: AiApplicationInterfaceTypeSchema,
  authSummary: z.string().nullable().optional(),
  metadata: AiApplicationInterfaceMetadataSchema,
});

export const AiApplicationInterfaceCreateSchema =
  AiApplicationInterfaceBaseSchema.omit({
    id: true,
  }).extend({
    id: z.string().optional(),
  });

export const AiApplicationInterfaceSchema = AiApplicationInterfaceBaseSchema;

const AiApplicationMcpReadFieldsSchema = z.object({
  mcpTransport: AiApplicationMcpTransportSchema.nullable().optional(),
  mcpEnv: z.record(z.string(), z.string()).nullable().optional(),
  mcpServerId: z.string().nullable().optional(),
});

const AiApplicationMcpWriteFieldsSchema =
  AiApplicationMcpReadFieldsSchema.extend({
    mcpCredential: UpsertMcpCredentialSchema.nullable().optional(),
  });

export const AiApplicationSchema = z
  .object({
    id: z.string(),
    tenantId: z.string().nullable(),
    kind: AiApplicationKindSchema,
    name: z.string(),
    url: z.string(),
    environment: z.string(),
    owner: z.string().nullable(),
    description: z.string().nullable(),
    agentPurpose: z.string().nullable(),
    technicalContext: z.string().nullable(),
    documentationUrls: z.array(z.url()),
    codeDiscoveryHints: z.string().nullable(),
    credentialSummary: z.string().nullable(),
    discoveryMode: AiApplicationDiscoveryModeSchema,
    interfaceTypes: z.array(AiApplicationInterfaceTypeSchema),
    interfaces: z.array(AiApplicationInterfaceSchema),
    businessContext: z.string().nullable(),
    testingInstructions: z.string().nullable(),
    discoveryStatus: AiApplicationDiscoveryStatusSchema,
    smokeTestEnabled: z.boolean(),
    isEnabled: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .extend(AiApplicationMcpReadFieldsSchema.shape);

const CreateAiApplicationBodySchema = z
  .object({
    kind: AiApplicationKindSchema,
    name: z.string().min(1).max(120),
    url: z.string().min(1).max(2048),
    environment: z.string().min(1).max(80),
    description: z.string().max(1000).nullable().optional(),
    agentPurpose: z.string().max(2000).nullable().optional(),
    technicalContext: z.string().max(5000).nullable().optional(),
    documentationUrls: z.array(z.url()).default([]),
    codeDiscoveryHints: z.string().max(5000).nullable().optional(),
    credentialSummary: z.string().max(5000).nullable().optional(),
    discoveryMode: AiApplicationDiscoveryModeSchema.optional(),
    interfaceTypes: z.array(AiApplicationInterfaceTypeSchema).min(1).optional(),
    interfaces: z.array(AiApplicationInterfaceCreateSchema).default([]),
    businessContext: z.string().max(5000).nullable().optional(),
    testingInstructions: z.string().max(5000).nullable().optional(),
  })
  .extend(AiApplicationMcpWriteFieldsSchema.shape);

export const CreateAiApplicationSchema =
  CreateAiApplicationBodySchema.superRefine((data, context) => {
    if (data.kind === 'AGENT') {
      if (!z.string().url().safeParse(data.url).success) {
        context.addIssue({
          code: 'custom',
          message: 'A valid URL is required',
          path: ['url'],
        });
      }

      return;
    }

    if (!data.mcpTransport) {
      context.addIssue({
        code: 'custom',
        message: 'MCP transport is required for MCP server applications',
        path: ['mcpTransport'],
      });
    }

    if (!z.string().url().safeParse(data.url).success) {
      context.addIssue({
        code: 'custom',
        message: 'A valid MCP server URL is required',
        path: ['url'],
      });
    }
  });

export const UpdateAiApplicationSchema = CreateAiApplicationBodySchema.partial()
  .extend({
    isEnabled: z.boolean().optional(),
    smokeTestEnabled: z.boolean().optional(),
    deferAutoDiscovery: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    error: 'At least one field must be provided',
  });

export const AiApplicationListQuerySchema = PaginationQueryParamsSchema.extend({
  search: z.string().optional(),
  environment: z.string().optional(),
  isEnabled: z.coerce.boolean().optional(),
});

export const AiApplicationListResponseSchema = z.object({
  data: z.array(AiApplicationSchema),
  total: z.int().nonnegative(),
});

export const AiComponentSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  applicationId: z.string(),
  type: AiComponentTypeSchema,
  name: z.string(),
  description: z.string().nullable(),
  role: z.string().nullable(),
  interfaceType: AiApplicationInterfaceTypeSchema.nullable(),
  isInternal: z.boolean(),
  codeGraphId: z.string().nullable(),
  aiFlowGraphId: z.string().nullable(),
  canonicalKey: z.string().nullable(),
  provenance: AiComponentProvenanceSchema.nullable(),
  verificationStatus: AiComponentVerificationStatusSchema.nullable(),
  runtimeOverlay: JsonValueSchema.nullable(),
  endpointId: z.string().nullable(),
  sources: z.array(AiComponentSourceSchema),
  graphStaleAt: z.coerce.date().nullable(),
  metadata: JsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const AiComponentRelationSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  applicationId: z.string(),
  sourceId: z.string(),
  targetId: z.string(),
  type: AiComponentRelationTypeSchema,
  label: z.string().nullable(),
  metadata: JsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
});

export const AiApplicationComponentsResponseSchema = z.object({
  components: z.array(AiComponentSchema),
  relations: z.array(AiComponentRelationSchema),
});

export const AiApplicationStatsSchema = z.object({
  agents: z.int().nonnegative(),
  components: z.int().nonnegative(),
  signals: z.int().nonnegative(),
  findings: z.int().nonnegative(),
  criticalFindings: z.int().nonnegative(),
  highFindings: z.int().nonnegative(),
  mediumFindings: z.int().nonnegative(),
  lowFindings: z.int().nonnegative(),
  executions: z.int().nonnegative(),
});

export const AiApplicationUrlEditRestrictionSchema = z.object({
  canEditUrl: z.boolean(),
  reason: z.string().optional(),
  componentsCount: z.number(),
  discoveryHasStarted: z.boolean(),
});

export const DeleteAiApplicationResponseSchema = z.object({
  message: z.string(),
});

export const AiDiscoveryTriggerResponseSchema = z.object({
  status: z.enum(['started', 'already_running']),
});

export const AiDiscoveryStopResponseSchema = z.object({
  status: z.enum(['stopped', 'not_running']),
});

export const AiDiscoveryProbeRetryResponseSchema = z.object({
  status: z.enum(['started', 'already_pending']),
});

export const AiDiscoveryRunHistoryEntrySchema = z.object({
  id: z.string(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  status: z.enum(['RUNNING', 'FAILED', 'COMPLETED', 'ABORTED']),
  failureReason: z.string().nullish(),
  probeCount: z.number().int().nonnegative(),
  verifiedProbeCount: z.number().int().nonnegative(),
  failedProbeCount: z.number().int().nonnegative(),
});

export type AiDiscoveryRunHistoryEntry = z.infer<
  typeof AiDiscoveryRunHistoryEntrySchema
>;

export const AiDiscoverySummarySchema = z.object({
  discoveryStatus: z.enum(['PENDING', 'RUNNING', 'COMPLETE', 'FAILED']),
  pendingProbeCount: z.int().nonnegative(),
  verifiedProbeCount: z.int().nonnegative(),
  totalProbeCount: z.int().nonnegative(),
  activeExecutionCount: z.int().nonnegative().default(0),
  discoveryRunId: z.string().nullable().optional(),
  discoveryStartedAt: z.string().datetime().nullable().optional(),
  awaitingCodeDiscovery: z.boolean().default(false),
  discoveryRunHistory: z
    .array(AiDiscoveryRunHistoryEntrySchema)
    .optional()
    .default([]),
});

export const AiComponentReconciliationDuplicateSchema = z.object({
  canonicalKey: z.string(),
  componentIds: z.array(z.string()),
  names: z.array(z.string()),
});

export const AiComponentReconciliationReportSchema = z.object({
  duplicates: z.array(AiComponentReconciliationDuplicateSchema),
  gapCount: z.int().nonnegative(),
  contradictedCount: z.int().nonnegative(),
  suppressedCount: z.int().nonnegative(),
  unverifiedCount: z.int().nonnegative(),
});

export const MergeAiComponentsSchema = z.object({
  sourceIds: z.array(z.string()).min(1),
  targetId: z.string(),
});

export const MergeAiComponentsResponseSchema = z.object({
  targetId: z.string(),
  mergedCount: z.int().nonnegative(),
});

export const CreateAiComponentSchema = z.object({
  type: AiComponentTypeSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  role: z.string().max(200).nullable().optional(),
});

export const UpdateAiComponentSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  verificationStatus: AiComponentVerificationStatusSchema.optional(),
  suppress: z.boolean().optional(),
});

export const CopyAiApplicationFromWebApplicationResponseSchema = z.object({
  aiApplicationId: z.string(),
  sourceApplicationId: z.string(),
});

export const WebApplicationMcpConnectionPrefillSchema = z
  .object({
    url: z.string(),
    transport: AiApplicationMcpTransportSchema,
  })
  .nullable();
